import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

const OFFSET = 8;
const MARGIN = 8;

/**
 * Reusable hover/focus tooltip. Wrap any control:
 * `<app-tooltip label="New chat"><button type="button">…</button></app-tooltip>`
 * The host uses `display: contents`, so the wrapper does not affect the
 * parent's layout. The bubble is positioned with fixed coordinates measured
 * from the wrapped element, so it works regardless of the host layout.
 */
@Component({
  selector: 'app-tooltip',
  template: `
    <ng-content />
    @if (label) {
      <span
        class="tooltip"
        [class.tooltip--visible]="visible"
        [class.tooltip--top]="placement === 'top'"
        [class.tooltip--bottom]="placement === 'bottom'"
        [class.tooltip--left]="placement === 'left'"
        [class.tooltip--right]="placement === 'right'"
        [style.left.px]="pos.x"
        [style.top.px]="pos.y"
        aria-hidden="true"
      >{{ label }}</span>
    }
  `,
  styleUrl: './tooltip.component.scss',
})
export class TooltipComponent implements OnInit, OnDestroy {
  /** Text shown in the bubble. Leave empty to render no tooltip. */
  @Input() label = '';
  @Input() placement: TooltipPlacement = 'right';
  /** When true the tooltip never shows (e.g. labels already visible). */
  @Input() disabled = false;

  protected visible = false;
  protected pos = { x: 0, y: 0 };

  private hideOnScroll = (): void => {
    this.visible = false;
  };

  private hideOnResize = (): void => {
    this.visible = false;
  };

  constructor(private readonly el: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    // Capture phase so inner scroll containers (not just the window) hide it.
    document.addEventListener('scroll', this.hideOnScroll, { capture: true, passive: true });
    window.addEventListener('resize', this.hideOnResize);
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.hideOnScroll, { capture: true });
    window.removeEventListener('resize', this.hideOnResize);
  }

  @HostListener('mouseenter')
  @HostListener('focusin')
  protected onShow(): void {
    this.show();
  }

  @HostListener('mouseleave')
  @HostListener('focusout')
  @HostListener('document:keydown.escape')
  protected onHide(): void {
    this.visible = false;
  }

  private target(): HTMLElement | null {
    return (this.el.nativeElement.firstElementChild ?? null) as HTMLElement | null;
  }

  private tip(): HTMLElement | null {
    return this.el.nativeElement.querySelector('.tooltip');
  }

  private show(): void {
    if (this.disabled || !this.label) {
      return;
    }
    const target = this.target();
    const tip = this.tip();
    if (!target || !tip) {
      return;
    }
    const rect = target.getBoundingClientRect();
    const w = tip.getBoundingClientRect().width;
    const h = tip.getBoundingClientRect().height;
    // Intended top-left corner of the bubble for the requested placement.
    let left = 0;
    let top = 0;
    switch (this.placement) {
      case 'left':
        left = rect.left - OFFSET;
        top = rect.top + rect.height / 2 - h / 2;
        break;
      case 'top':
        left = rect.left + rect.width / 2 - w / 2;
        top = rect.top - OFFSET - h;
        break;
      case 'bottom':
        left = rect.left + rect.width / 2 - w / 2;
        top = rect.bottom + OFFSET;
        break;
      default:
        left = rect.right + OFFSET;
        top = rect.top + rect.height / 2 - h / 2;
    }
    // Keep the bubble fully within the viewport.
    const margin = MARGIN;
    left = Math.min(Math.max(margin, left), window.innerWidth - w - margin);
    top = Math.min(Math.max(margin, top), window.innerHeight - h - margin);
    // Derive the inline anchor (pos) from the clamped top-left corner.
    switch (this.placement) {
      case 'left':
        this.pos = { x: left + w, y: top + h / 2 };
        break;
      case 'top':
        this.pos = { x: left + w / 2, y: top + h };
        break;
      case 'bottom':
        this.pos = { x: left + w / 2, y: top };
        break;
      default:
        this.pos = { x: left, y: top + h / 2 };
    }
    this.visible = true;
  }
}
